import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  Input,
  OnDestroy,
} from '@angular/core';
import { ModalHeaderComponent } from '@base/components/private/modals/modal-header/modal-header.component';
import { ModalBodyComponent } from '@base/components/private/modals/modal-body/modal-body.component';
import { TranslatePipe } from '@base/pipes/private/translate.pipe';
import { AgendaInterest } from '@base/models/responses/agenda-session.res';
import { ModalService } from '@base/services/private/modal.service';
import { AgendaService } from '@base/services/private/agenda.service';
import { NgxSkeletonLoaderComponent } from 'ngx-skeleton-loader';
import { FormsModule } from '@angular/forms';
import { GlobalStateService } from '@base/services/private/global-state.service';
import { ModalFooterComponent } from '@base/components/private/modals/modal-footer/modal-footer.component';
import { GlobalService } from '@base/services/private/global.service';

@Component({
  selector: 'app-modal-agenda-interest',
  imports: [
    ModalHeaderComponent,
    ModalBodyComponent,
    forwardRef(() => TranslatePipe),
    NgxSkeletonLoaderComponent,
    FormsModule,
    ModalFooterComponent,
  ],
  templateUrl: './modal-agenda-interest.component.html',
  styleUrl: './modal-agenda-interest.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalAgendaInterestComponent implements AfterViewInit, OnDestroy {
  private readonly modalService = inject(ModalService);
  private readonly agendaService = inject(AgendaService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly globalStateSrv = inject(GlobalStateService);
  private readonly globalSrv = inject(GlobalService);

  detail: AgendaInterest[] = [];
  selected: AgendaInterest[] = [];
  @Input() modalId = 'modal-agenda-interest';
  errorMessage = '';
  validationMessage = '';
  hasAttemptedSubmit = false;
  isContentLoaded = false;
  isHasMeetInterest = false;
  searchTerm = '';
  filteredList: AgendaInterest[] = [];
  isHasAgendaInterest = this.globalStateSrv.isHasAgendaInterest();

  ngAfterViewInit() {
    this.getAgendaInterest();
  }

  ngOnDestroy(): void {
    this.globalSrv.destroyModalRef(this.modalId);
  }

  /**
   * Fetch agenda interests from the service and populate the detail and selected arrays.
   * Handles success and error scenarios, updating the component state accordingly.
   */
  getAgendaInterest() {
    this.agendaService.getAgendaInterestForYou().subscribe({
      next: (response) => {
        this.isContentLoaded = true;
        this.detail = response?.event_keywords.map((keyword) => ({ id: keyword, value: keyword })) || [];
        this.filteredList = this.detail;
        this.selected = response?.my_keywords?.map((keyword) => ({ id: keyword, value: keyword })) || [];
        this.isHasAgendaInterest = this.selected.length > 0;
        this.changeDetectorRef.markForCheck();
        this.changeDetectorRef.detectChanges();
      },
      error: (err) => {
        console.error('Error fetching speaker populate filter:', err);
        this.errorMessage = 'Failed to load interests. Please try again later.';
        this.changeDetectorRef.markForCheck();
        this.changeDetectorRef.detectChanges();
      },
    });
  }

  /**
   * Update the user's agenda interests by sending the selected IDs to the service.
   * Closes the modal upon successful update.
   * Handles error scenarios by logging the error.
   */
  update() {
    this.hasAttemptedSubmit = true;
    this.refreshValidationMessage();

    if (this.selected.length === 0) return;

    const selectedIds = this.selected.map((item) => item.id);
    this.agendaService.updateAgendaInterest(selectedIds).subscribe({
      next: (response) => {
        if (response.status) {
          this.modalService.close(this.modalId, { key: 'MODAL_AGENDA_INTEREST' });
        }
      },
      error: (err) => {
        console.error('Error updating agenda interests:', err);
      },
    });
  }

  /**
   * Checks if an agenda interest item is selected.
   * @param item - The agenda interest item to check.
   * @returns True if the item is selected, false otherwise.
   */
  isSelected(item: AgendaInterest): boolean {
    return this.selected.some((selectedItem) => selectedItem.id === item.id);
  }

  /**
   * Toggles the selection state of the given agenda interest item.
   * @param item - The agenda interest item to select or deselect.
   */
  selectFilter(item: AgendaInterest) {
    if (this.isSelected(item)) {
      this.selected = this.selected.filter((selectedItem) => selectedItem.id !== item.id);
    } else {
      this.selected.push(item);
    }

    if (this.hasAttemptedSubmit) {
      // Keep the message in sync with the selection after the first submit attempt.
      this.refreshValidationMessage();
    }
  }

  /**
   * Shows the message while nothing is selected, clears it once something is.
   * @returns void
   */
  private refreshValidationMessage(): void {
    this.validationMessage = this.selected.length === 0 ? 'INTEREST_CANNOT_EMPTY' : '';
  }

  /**
   * Closes the modal by invoking the ModalService with the modal ID.
   * @returns void
   */
  close(): void {
    this.modalService.close(this.modalId);
  }

  /**
   * onchange search interest
   */
  onSearchChange() {
    this.filteredList = this.detail.filter((item) => item.value.toLowerCase().includes(this.searchTerm.toLowerCase()));
  }
}
